import { ArrowUpRight, ChevronRight, Code2, Link2, Repeat, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { CopyIcon } from '@/components/CopyIcon';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-dialog';
import {
  WebhookDeliveryStatus,
  type LinkedApiRequestDto,
  type WebhookDeliveryDto,
  type WebhookEventDetailDto,
} from '@/lib/api/schemas';
import { formatBytes, tryPrettyJson } from '@/lib/json/pretty';
import { cn } from '@/lib/utils';

import { DeliverySummaryPips } from './DeliverySummaryPips';
import { WebhookStatusBadge } from './WebhookStatusBadge';

interface Props {
  detail: WebhookEventDetailDto;
  /** Whether the current user can fire replay actions (Admin / ClientAdmin). */
  canReplay: boolean;
  /** Tooltip shown when replay buttons are disabled. */
  replayDisabledHint?: string;
  /** True while a per-delivery replay mutation is in flight. */
  isReplayingDelivery?: boolean;
  /** True while the replay-all-failed mutation is in flight. */
  isReplayingAll?: boolean;
  /** Map of delivery-id → true while THAT delivery just succeeded — fades after ~1.2s. */
  recentlyReplayedIds?: Set<string>;
  onReplayDelivery: (deliveryId: string) => void;
  onReplayAllFailed: () => void;

  // ---- "Originating API calls" linkage section ----
  // Optional. When omitted the section is hidden (back-compat for tests / pages
  // that don't fetch the linkage). Admin pages fetch via the admin endpoint and
  // build links to /transactions/{id}; portal pages fetch via the portal
  // endpoint and link to /portal/transactions/{id}.
  /** Rows from GET /…/webhooks/events/{id}/api-requests. Undefined while loading. */
  linkedApiRequests?: LinkedApiRequestDto[];
  /** True while the api-requests query is loading. */
  isLoadingLinkedApiRequests?: boolean;
  /** Build the transactions detail href for a row. Admin uses /transactions/{id}; portal uses /portal/transactions/{id}. */
  buildTransactionHref?: (apiRequestId: string) => string;
}

/**
 * Drop-in detail panel rendered inside the expanded row of the Webhooks list.
 * Pure presentation — the parent wires the orval mutations and feeds back
 * pending/success state via props.
 */
export function WebhookEventDetailPanel({
  detail,
  canReplay,
  replayDisabledHint,
  isReplayingDelivery,
  isReplayingAll,
  recentlyReplayedIds,
  onReplayDelivery,
  onReplayAllFailed,
  linkedApiRequests,
  isLoadingLinkedApiRequests,
  buildTransactionHref,
}: Props) {
  const { summary, rawNotificationItemJson, rawAdditionalDataJson, deliveries } = detail;
  const showLinkageSection = linkedApiRequests !== undefined || isLoadingLinkedApiRequests === true;
  const confirm = useConfirm();

  const hasAnyFailed = deliveries.some(
    (d) => d.status === WebhookDeliveryStatus.Failed || d.status === WebhookDeliveryStatus.Abandoned,
  );

  return (
    <div className="space-y-4">
      <div className="grid gap-3 text-xs md:grid-cols-3">
        <KV k="Event ID" v={summary.id} mono />
        <KV k="Client ID" v={summary.clientId} mono />
        <KV k="Merchant" v={`${summary.merchantCode} (${summary.merchantId})`} mono />
        <KV k="Event code" v={summary.eventCode} />
        <KV k="PSP reference" v={summary.pspReference ?? '—'} mono />
        <KV k="Original reference" v={summary.originalReference ?? '—'} mono />
        <KV k="Success flag" v={summary.success ? 'true' : 'false'} />
        <KV k="Created" v={new Date(summary.createdAt).toISOString()} mono />
      </div>

      <PayloadSection
        label="Inbound notificationItem"
        body={rawNotificationItemJson}
        defaultOpen={false}
      />
      {rawAdditionalDataJson ? (
        <PayloadSection
          label="additionalData"
          body={rawAdditionalDataJson}
          defaultOpen={false}
        />
      ) : null}

      {showLinkageSection ? (
        <LinkedApiRequestsSection
          rows={linkedApiRequests}
          isLoading={!!isLoadingLinkedApiRequests}
          buildHref={buildTransactionHref}
        />
      ) : null}

      <div>
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Deliveries
            </span>
            <DeliverySummaryPips
              total={summary.deliveryCount}
              delivered={summary.deliveredCount}
              failed={summary.failedCount}
              abandoned={summary.abandonedCount}
            />
          </div>
          {hasAnyFailed && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={!canReplay || isReplayingAll}
              title={!canReplay ? replayDisabledHint : undefined}
              onClick={async () => {
                if (!canReplay) return;
                const ok = await confirm({
                  title: 'Replay all failed deliveries?',
                  description:
                    'Every Failed and Abandoned delivery for this event will be re-queued for the dispatcher. Already-Delivered rows are untouched.',
                  confirmLabel: 'Replay all',
                });
                if (ok) onReplayAllFailed();
              }}
            >
              {!canReplay && <ShieldAlert className="h-3.5 w-3.5" />}
              <Repeat className="h-3.5 w-3.5" />
              {isReplayingAll ? 'Replaying…' : 'Replay all failed'}
            </Button>
          )}
        </div>

        {deliveries.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-muted/20 p-4 text-center text-xs text-muted-foreground">
            No deliveries scheduled — the event has no matching webhook endpoints.
          </div>
        ) : (
          <div className="space-y-2">
            {deliveries.map((d) => (
              <DeliveryCard
                key={d.id}
                delivery={d}
                canReplay={canReplay}
                replayDisabledHint={replayDisabledHint}
                isReplaying={!!isReplayingDelivery}
                justReplayed={!!recentlyReplayedIds?.has(d.id)}
                onReplay={() => onReplayDelivery(d.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface DeliveryCardProps {
  delivery: WebhookDeliveryDto;
  canReplay: boolean;
  replayDisabledHint?: string;
  isReplaying: boolean;
  justReplayed: boolean;
  onReplay: () => void;
}

function DeliveryCard({
  delivery,
  canReplay,
  replayDisabledHint,
  isReplaying,
  justReplayed,
  onReplay,
}: DeliveryCardProps) {
  const confirm = useConfirm();
  const canShowReplay =
    delivery.status === WebhookDeliveryStatus.Failed ||
    delivery.status === WebhookDeliveryStatus.Abandoned;

  return (
    <div
      className={cn(
        'rounded-md border border-border bg-background/40 p-3',
        justReplayed && 'motion-safe:animate-pulse ring-1 ring-emerald-500/40',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <WebhookStatusBadge kind="delivery" status={delivery.status} />
            <span className="truncate font-mono text-xs text-foreground" title={delivery.endpointUrlSnapshot}>
              {delivery.endpointUrlSnapshot}
            </span>
            <span className="text-[11px] tabular-nums text-muted-foreground">
              attempt {Number(delivery.attemptCount)}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            {delivery.lastResponseStatus !== null && delivery.lastResponseStatus !== undefined ? (
              <span className="tabular-nums">HTTP {Number(delivery.lastResponseStatus)}</span>
            ) : null}
            {delivery.lastDurationMs !== null && delivery.lastDurationMs !== undefined ? (
              <span className="tabular-nums">{Number(delivery.lastDurationMs)} ms</span>
            ) : null}
            {delivery.nextRetryAt ? (
              <span>next retry {formatRel(delivery.nextRetryAt)}</span>
            ) : null}
            {delivery.lastAttemptAt ? (
              <span>last {formatRel(delivery.lastAttemptAt)}</span>
            ) : null}
            {delivery.abandonReason ? (
              <span className="text-rose-600 dark:text-rose-400">
                Abandoned: {delivery.abandonReason}
              </span>
            ) : null}
          </div>
          {delivery.lastError ? (
            <div className="rounded-sm border border-rose-200/50 bg-rose-500/5 p-2 font-mono text-[11px] text-rose-700 dark:border-rose-400/20 dark:text-rose-300">
              {delivery.lastError}
            </div>
          ) : null}
        </div>
        {canShowReplay && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 gap-1.5"
            disabled={!canReplay || isReplaying}
            title={!canReplay ? replayDisabledHint : undefined}
            onClick={async () => {
              if (!canReplay) return;
              const ok = await confirm({
                title: 'Replay this delivery?',
                description:
                  'This delivery will be re-queued for the dispatcher and a fresh attempt will fire on the next worker tick.',
                confirmLabel: 'Replay',
              });
              if (ok) onReplay();
            }}
          >
            {!canReplay && <ShieldAlert className="h-3.5 w-3.5" />}
            <Repeat className="h-3.5 w-3.5" />
            {isReplaying ? 'Replaying…' : 'Replay'}
          </Button>
        )}
      </div>

      {delivery.history && delivery.history.length > 0 ? (
        <details className="group mt-2 rounded-sm border-t border-border/50 pt-2">
          <summary className="flex cursor-pointer list-none items-center gap-2 text-[11px] text-muted-foreground">
            <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
            Attempt history ({delivery.history.length})
          </summary>
          <table className="mt-1 w-full text-[11px]">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1 pr-2 font-medium">#</th>
                <th className="py-1 pr-2 font-medium">When</th>
                <th className="py-1 pr-2 font-medium">HTTP</th>
                <th className="py-1 pr-2 font-medium">Duration</th>
                <th className="py-1 pr-2 font-medium">Error</th>
              </tr>
            </thead>
            <tbody>
              {delivery.history.map((h, idx) => (
                <tr key={`${h.attempt}-${idx}`} className="border-t border-border/40">
                  <td className="py-1 pr-2 tabular-nums">{Number(h.attempt)}</td>
                  <td className="py-1 pr-2 font-mono">{new Date(h.attemptedAt).toISOString()}</td>
                  <td className="py-1 pr-2 tabular-nums">
                    {h.httpStatus !== null && h.httpStatus !== undefined ? Number(h.httpStatus) : '—'}
                  </td>
                  <td className="py-1 pr-2 tabular-nums">
                    {h.durationMs !== null && h.durationMs !== undefined ? `${Number(h.durationMs)} ms` : '—'}
                  </td>
                  <td className="py-1 pr-2 text-muted-foreground">{h.error ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      ) : null}
    </div>
  );
}

/**
 * Renders the api_requests rows that match this webhook's pspReference. Joined
 * server-side via `webhook_events.psp_reference` / `original_reference` →
 * `api_requests.psp_reference`. Empty list is a normal state for routes that
 * don't surface pspReference (paymentMethods, sessions) and for events from
 * before psp_reference capture shipped.
 */
function LinkedApiRequestsSection({
  rows,
  isLoading,
  buildHref,
}: {
  rows: LinkedApiRequestDto[] | undefined;
  isLoading: boolean;
  buildHref?: (id: string) => string;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Originating API calls
        </span>
        {rows !== undefined && (
          <span className="text-[11px] tabular-nums text-muted-foreground">
            ({rows.length})
          </span>
        )}
      </div>
      {isLoading ? (
        <div className="rounded-md border border-dashed border-border bg-muted/20 p-3 text-center text-xs text-muted-foreground">
          Loading…
        </div>
      ) : !rows || rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-muted/20 p-3 text-center text-xs text-muted-foreground">
          No linked API calls found for this PSP reference. (Normal for{' '}
          <code className="font-mono">/paymentMethods</code>,{' '}
          <code className="font-mono">/sessions</code>, or events received before
          psp_reference capture shipped.)
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-xs">
            <thead className="bg-muted/30 text-left text-muted-foreground">
              <tr>
                <th className="px-2 py-1 font-medium">When</th>
                <th className="px-2 py-1 font-medium">Method</th>
                <th className="px-2 py-1 font-medium">Endpoint</th>
                <th className="px-2 py-1 font-medium">Status</th>
                <th className="px-2 py-1 font-medium">Merchant</th>
                <th className="px-2 py-1 font-medium">PSP ref</th>
                <th className="px-2 py-1" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const href = buildHref?.(r.id);
                return (
                  <tr key={r.id} className="border-t border-border/40">
                    <td className="px-2 py-1 font-mono text-[11px] text-muted-foreground">
                      {new Date(r.createdAt).toISOString()}
                    </td>
                    <td className="px-2 py-1 font-mono">{r.method}</td>
                    <td className="px-2 py-1 font-mono text-[11px]">{r.endpoint}</td>
                    <td className="px-2 py-1 tabular-nums">{Number(r.statusCode)}</td>
                    <td className="px-2 py-1 font-mono text-[11px]">{r.merchantId ?? '—'}</td>
                    <td className="px-2 py-1 font-mono text-[11px]">{r.pspReference ?? '—'}</td>
                    <td className="px-2 py-1 text-right">
                      {href ? (
                        <Link
                          to={href}
                          className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                        >
                          Open
                          <ArrowUpRight className="h-3 w-3" />
                        </Link>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PayloadSection({
  label,
  body,
  defaultOpen,
}: {
  label: string;
  body: string;
  defaultOpen: boolean;
}) {
  const pretty = tryPrettyJson(body);
  const [copied, setCopied] = useState(false);
  return (
    <details
      open={defaultOpen}
      className="group rounded-md border border-border bg-muted/20"
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 p-2 text-xs">
        <span className="flex items-center gap-2">
          <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
          <Code2 className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-medium">{label}</span>
          <span className="text-[11px] text-muted-foreground">— {formatBytes(body.length)}</span>
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 gap-1 px-2 text-[11px]"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void navigator.clipboard
              .writeText(body)
              .then(() => {
                setCopied(true);
                toast.success('Copied to clipboard.');
              })
              .catch(() => toast.error('Could not copy to clipboard.'));
          }}
        >
          <CopyIcon copied={copied} onRevert={() => setCopied(false)} className="h-3 w-3" />
          Copy
        </Button>
      </summary>
      <pre className="overflow-x-auto rounded-md bg-background/60 p-3 font-mono text-[11px] leading-relaxed">
        {pretty}
      </pre>
    </details>
  );
}

function KV({ k, v, mono = false }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</span>
      <span className={cn('break-all', mono && 'font-mono')}>{v}</span>
    </div>
  );
}

function formatRel(iso: string): string {
  const d = new Date(iso);
  const ms = d.getTime() - Date.now();
  const abs = Math.abs(ms);
  const min = Math.round(abs / 60000);
  if (min < 1) return ms >= 0 ? 'shortly' : 'just now';
  if (min < 60) return ms >= 0 ? `in ${min}m` : `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return ms >= 0 ? `in ${hr}h` : `${hr}h ago`;
  const day = Math.round(hr / 24);
  return ms >= 0 ? `in ${day}d` : `${day}d ago`;
}
